import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { Lock, Database, Terminal, Rocket, Eye } from 'lucide-react';
import { selectImageTierVisible, selectIsTargetModeReady } from '../../redux/selectors';
import { paths } from '../../routes';

// Wraps authoring routes that must not render on a consumer target: a direct
// URL lands on a clear unavailable state instead of a blank/half-editable
// editor. Renders a spinner until the mode resolves so an editor never paints
// before the capability is known; builder renders children unchanged.
// `fallback` lets a route offer a consumer target something real in place of the
// locked panel — used by Agents, where the target genuinely has a read-only
// roster to show and "not available" would be a dead end rather than the truth.
// Omitted, the guard behaves exactly as before.
const ConsumerImageGuard = ({ children, fallback = null }) => {
  const ready = useSelector(selectIsTargetModeReady);
  const imageTierVisible = useSelector(selectImageTierVisible);

  if (!ready) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[240px] bg-slate-50 dark:bg-slate-950 grey:bg-zinc-200">
        <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (imageTierVisible) return children;

  return fallback || <ConsumerUnavailable />;
};

// "Not available on this target" panel with links to the surfaces a consumer
// target can use: the deployed app's own diagram, Target Configuration,
// Variables, Releases.
//
// Deployed App Detail is listed first and only for app-scoped routes: the
// commonest way to land here is the locked Deploy page, and the question that
// brought you there — what is this app actually running? — has a real answer on
// a consumer target. That page reads the appconfig snapshot, carries no
// authoring, and is NOT image-tier; without this link it is reachable only by
// typing the URL, since its only other entry point is a button on the very page
// this panel replaces.
const ConsumerUnavailable = () => {
  const { orgId, appId } = useParams();
  const links = [
    orgId && appId && { to: paths.deployedOverview(orgId, appId), icon: Eye, label: 'Deployed App Detail', desc: 'Read-only diagram of what is running' },
    orgId && appId && { to: paths.app(orgId, appId), icon: Database, label: 'Target Configuration', desc: 'Datasets & connections' },
    orgId && appId && { to: `${paths.app(orgId, appId)}/variables`, icon: Terminal, label: 'Variables', desc: 'Config variables & secrets' },
    orgId && { to: paths.releases(orgId), icon: Rocket, label: 'Releases', desc: 'Deploy & monitor releases' },
  ].filter(Boolean);

  return (
    <div className="flex flex-col items-center justify-center w-full h-full min-h-[320px] text-center p-8 bg-slate-50 dark:bg-slate-950 grey:bg-zinc-200">
      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 grey:bg-zinc-300 rounded-full flex items-center justify-center mb-5">
        <Lock size={26} className="text-slate-400 dark:text-slate-500 grey:text-zinc-500" strokeWidth={2} />
      </div>
      <h2 className="text-lg font-bold text-slate-700 dark:text-slate-200 grey:text-zinc-800 mb-2">
        Not available on this target
      </h2>
      <p className="text-xs text-slate-400 dark:text-slate-500 grey:text-zinc-600 max-w-md mb-6 leading-relaxed">
        This is a consumer target. Application authoring — editors, screens, views,
        themes, branches, deploys, and templates — is managed upstream and locked here.
        You can still provision and monitor this target.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {links.map(({ to, icon: Icon, label, desc }) => (
          <Link
            key={to}
            to={to}
            className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-900 grey:bg-zinc-100 border border-slate-200 dark:border-slate-800 grey:border-zinc-300 rounded-xl shadow-sm hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-all text-left"
          >
            <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 grey:bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Icon size={16} className="text-indigo-500" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 grey:text-zinc-800">{label}</div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500 grey:text-zinc-600">{desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ConsumerImageGuard;
